import React, { useEffect, useState } from 'react';

//icons
import locationIcon from 'assets/icons/location.svg';
import closeIcon from 'assets/icons/closeIcon.svg';
import editIcon from 'assets/icons/edit.svg';
import searchIcon from 'assets/icons/search.svg';

//components
import InputGroup from 'components/input-group';
import UserProfile from 'components/user-profile';
import fundmoreai from 'assets/fundmoreai.png';

//libraries
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';

//framer animations
import { scaleMotionProps2 } from 'constants/animationProps';

//API queries
import { useGetLoanOfficersQuery } from 'redux_store/services/client/onboarding';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/features/onboarding-layout.module.css';
/**
 * Page3 function
 *
 * This function is a React component that represents the third page of a multi-step form.
 * It takes several props as input and renders a form with various input fields and buttons.
 *
 * Props:
 * - user: The user's name
 * - address: The user's address
 * - selectedOfficer: The selected loan officer
 * - setSelectedOfficer: A function to set the selected loan officer
 * - setSelectedOfficerObj: A function to set the selected loan officer object
 * - selectedOfficerObj: The selected loan officer object
 * - selectedOption: A boolean indicating whether a loan officer is selected
 * - setPage: A function to set the current page
 * - isLoading: A boolean indicating whether data is being loaded
 * - setDirection: A function to set the direction of the form navigation
 * - direction: A boolean indicating the direction of the form navigation
 *
 * Returns:
 * The rendered React component.
 *
 * Usage:
 * <Page3
 *   user={user}
 *   address={address}
 *   selectedOfficer={selectedOfficer}
 *   setSelectedOfficer={setSelectedOfficer}
 *   setSelectedOfficerObj={setSelectedOfficerObj}
 *   selectedOfficerObj={selectedOfficerObj}
 *   selectedOption={selectedOption}
 *   setPage={setPage}
 *   isLoading={isLoading}
 *   setDirection={setDirection}
 *   direction={direction}
 * />
 */
const Page3 = ({
  user,
  address,
  selectedOfficer,
  setSelectedOfficer,
  setSelectedOfficerObj,
  selectedOfficerObj,
  selectedOption,
  setPage,
  isLoading: isUpdateLoading,
  setDirection,
  direction,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  //states
  const { data, isLoading, refetch } = useGetLoanOfficersQuery();
  const [showComponent, setShowComponent] = useState(false);
  const [enteredData, setEnteredData] = useState('');
  const [filteredProfiles, setFilteredProfiles] = useState([]);

  const handleOfficerChange = (event) => {
    setSelectedOfficer(event.target.value);
  };

  useEffect(() => {
    if (enteredData) {
      const enteredDataLower = enteredData.toLowerCase();
      const matchingProfiles = data?.data?.users?.filter((profile) => {
        const fullName =
          `${profile.firstName} ${profile.lastName}`.toLowerCase();
        return fullName.includes(enteredDataLower);
      });

      setFilteredProfiles(matchingProfiles);
      setShowComponent(matchingProfiles.length > 0);
    } else if (!address && selectedOfficerObj) {
      setShowComponent(true);
    } else {
      setFilteredProfiles([]);
      setShowComponent(false);
    }
  }, [enteredData]);

  const handleSearchChange = () => {
    setEnteredData(selectedOfficer);
  };

  useEffect(() => {
    if (data?.data?.users) {
      let filteredProfiles = data.data.users.filter((profile) => {
        const fullName = `${profile.firstName} ${profile.lastName}`;
        return fullName.toLowerCase().includes(selectedOfficer?.toLowerCase());
      });

      if (selectedOfficerObj) {
        filteredProfiles = filteredProfiles.filter((profile) => {
          return profile.userId === selectedOfficerObj.userId;
        });
      }

      setFilteredProfiles(filteredProfiles);
    }
  }, [data, selectedOfficer, selectedOfficerObj]);

  return (
    <div className={styles.contentWrapper}>
      <AnimatePresence mode="wait">
        <motion.div
          {...scaleMotionProps2}
          className={`${styles.formContainer} ${styles.fadeInContent}`}
        >
          <div className={styles.logoContainer}>
            <img src={fundmoreai} alt="Fundmore" className={styles.logoIcon} />
            <div className={styles.logoText}>
              <span className={styles.logoTextLight}>fund</span>
              <span className={styles.logoTextBold}>more</span>
              <span className={styles.logoTextGreen}>.ai</span>
            </div>
          </div>
          <span className={styles.srOnly}>
            {t('onboarding:greeting', { name: user })}
          </span>

          <div className={styles.progressDotsContainer}>
            <div className={`${styles.progressDot} ${styles.progressDotInactive}`} />
            <div className={`${styles.progressDot} ${styles.progressDotInactive}`} />
            <div className={`${styles.progressDot} ${styles.progressDotActive}`} />
          </div>

          <h2 className={styles.heading}>
            {t('onboarding:chooseOfficerTitle')}
          </h2>
          <p className={styles.subheading}>
            {t('onboarding:chooseOfficerSubtitle')}
          </p>

          {address && (
            <div className={styles.addressRow}>
              <img src={locationIcon} alt="" />
              <span className={styles.addressValue}>{address}</span>
              <img
                src={editIcon}
                alt=""
                onClick={() => setPage(2)}
                className={styles.addressEditIcon}
              />
            </div>
          )}

          <div className={styles.optionsContainer}>
            {selectedOption && (
              <div className={styles.inputWrapper}>
                <InputGroup
                  size="m"
                  position="center"
                  placeholder={t('onboarding:searchBrokerPlaceholder')}
                  svg={searchIcon}
                  value={selectedOfficer}
                  onChange={handleOfficerChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleSearchChange();
                    }
                  }}
                />
                {selectedOfficer && (
                  <img
                    src={closeIcon}
                    alt=""
                    className={styles.clearIcon}
                    onClick={() => {
                      setSelectedOfficer('');
                      setSelectedOfficerObj(null);
                      setEnteredData('');
                      setShowComponent(false);
                    }}
                  />
                )}
              </div>
            )}
            {!selectedOption && (
              <div className={styles.inputWrapper}>
                <InputGroup
                  size="m"
                  position="center"
                  placeholder={t('onboarding:searchPlaceholder')}
                  svg={searchIcon}
                  value={selectedOfficer}
                  onChange={handleOfficerChange}
                />
                {selectedOfficer && (
                  <img
                    src={closeIcon}
                    alt=""
                    className={styles.clearIcon}
                    onClick={() => {
                      setSelectedOfficer('');
                      setSelectedOfficerObj(null);
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {showComponent && (
            <div
              className={`${styles.profileList} ${
                filteredProfiles.length <= 6
                  ? styles.profileListCentered
                  : styles.profileListStart
              }`}
            >
              {filteredProfiles?.map((profile, index) => (
                <UserProfile
                  key={index}
                  firstName={profile.firstName}
                  lastName={profile.lastName}
                  loanOfficer={''}
                  selected={selectedOfficerObj?.userId === profile?.userId}
                  className={`${styles.userProfileCard} ${
                    selectedOfficerObj?.userId === profile?.userId
                      ? styles.userProfileCardSelected
                      : ''
                  }`}
                  onClick={() => {
                    setSelectedOfficer(
                      profile.firstName + ' ' + profile.lastName
                    );
                    setSelectedOfficerObj(profile);
                  }}
                />
              ))}
            </div>
          )}
          {address && (
            <div
              className={`${styles.profileList} ${
                filteredProfiles.length <= 6
                  ? styles.profileListCentered
                  : styles.profileListStart
              }`}
            >
              {filteredProfiles?.map((profile, index) => (
                <UserProfile
                  key={index}
                  firstName={profile.firstName}
                  lastName={profile.lastName}
                  loanOfficer={''}
                  selected={selectedOfficerObj?.userId === profile?.userId}
                  className={`${styles.userProfileCard} ${
                    selectedOfficerObj?.userId === profile?.userId
                      ? styles.userProfileCardSelected
                      : ''
                  }`}
                  onClick={() => {
                    setSelectedOfficer(
                      profile.firstName + ' ' + profile.lastName
                    );
                    setSelectedOfficerObj(profile);
                  }}
                />
              ))}
            </div>
          )}
          {!showComponent && enteredData && (
            <div className={styles.helperText}>
              {t('onboarding:noResults', { query: enteredData })}
            </div>
          )}
          {filteredProfiles?.length === 0 && address && data && (
            <div className={styles.helperText}>
              {t('onboarding:noResults', { query: selectedOfficer })}
            </div>
          )}

          <div className={`${styles.buttonContainer} ${styles.buttonGroup}`}>
            <button
              type="button"
              onClick={
                selectedOption
                  ? () => {
                      setPage(1);
                      setDirection(false);
                    }
                  : () => {
                      setPage(2);
                      setDirection(false);
                    }
              }
              className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonFlexOne}`}
            >
              {t('common:buttons.back')}
            </button>
            <button
              type="button"
              disabled={!selectedOfficerObj}
              onClick={() => {
                setPage(4);
                setDirection(true);
              }}
              className={`${styles.button} ${styles.buttonPrimary} ${styles.buttonFlexTwo}`}
            >
              {isUpdateLoading && direction
                ? t('common:buttons.pleaseWait')
                : t('common:buttons.continue')}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Page3;
